/**
 * This file is part of the Valkyrie Robotics public website.
 * © 2025 Valkyrie Robotics, LLC. All rights reserved.
 */

import { motion } from "framer-motion";
import { Section } from "../components/Section";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { FreeTrialSchedule } from "../components/FreeTrialSchedule";

export function HomePage() {
  return (
    <>
      <SEO
        title="Valkyrie Robotics"
        description="Competitive VEX Robotics coaching for students in Forsyth County, GA. We offer VEX IQ and V5 programs to help students develop STEM skills through hands-on robotics competitions."
        canonical="/"
        keywords={[
          "VEX Robotics",
          "robotics coaching",
          "STEM education",
          "competitive robotics",
          "VEX IQ",
          "VEX V5",
          "Forsyth County",
          "Georgia robotics",
          "student robotics",
          "robotics competition",
        ]}
      />
      <div className="bg-neutral-900">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="mx-auto max-w-6xl px-4 py-12 md:py-24 text-gray-100">
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="space-y-6 order-2 lg:order-1">
                <motion.h1
                  className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white text-center lg:text-left"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  Competitive Robotics for Students
                </motion.h1>
                <motion.p
                  className="text-lg md:text-xl text-gray-300 leading-relaxed text-center lg:text-left"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  Valkyrie Robotics is a coaching program for students
                  passionate about building, coding, and competing with VEX
                  robots. Led by experienced mentors, we prepare kids for
                  regional and world-class competitions while developing
                  technical and leadership skills.
                </motion.p>
                <motion.div
                  className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <motion.button
                    data-cal-namespace="free-trial"
                    data-cal-link="aplakosh/free-trial"
                    data-cal-config='{"layout":"month_view"}'
                    className="w-full sm:w-auto rounded-lg bg-brand-600 text-neutral-900 font-semibold px-6 md:px-8 py-3 md:py-4 text-base md:text-lg transition-all duration-150 hover:scale-105 hover:bg-brand-500 active:scale-95 shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Try for free
                  </motion.button>
                  <FreeTrialSchedule className="text-center lg:text-left" />
                  <motion.a
                    href="#what-we-offer"
                    className="w-full sm:w-auto rounded-lg border-2 border-neutral-600 text-gray-200 px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-medium transition-all duration-150 hover:scale-105 hover:border-brand-500 hover:text-brand-400 active:scale-95"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Explore Programs
                  </motion.a>
                </motion.div>
              </div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="relative order-1 lg:order-2 w-full max-w-md mx-auto lg:mx-0"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 to-transparent rounded-2xl"></div>
                <img
                  src="/media/hero.jpg"
                  alt="Students competing in VEX Robotics competition with remote controls and VEX parts"
                  className="relative w-full h-auto object-cover rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* How VEX Robotics Works Section */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 to-transparent rounded-2xl"></div>
                <img
                  src="/media/how-vex-works.jpg"
                  alt="Students working on VEX Robotics project"
                  className="relative w-full h-auto object-cover rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-100 mb-4">
                  How VEX Robotics Works
                </h2>
                <p className="text-lg text-brand-400 font-medium">
                  From Challenge to Championship
                </p>
              </div>
              <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  In VEX Robotics, students are introduced to the exciting world
                  of engineering, programming, and teamwork. The process starts
                  by forming teams of 4–6 students. Each team is given a unique
                  game challenge that changes every year, designed to test their
                  problem-solving and robotic design skills.
                </p>
                <p>
                  Over the span of several months, students work together to
                  build and program their robots, using the VEX IQ or VEX V5
                  systems to tackle the specific tasks outlined in the
                  challenge. Throughout the season, teams compete in multiple
                  local and regional competitions, gaining valuable experience
                  and refining their designs.
                </p>
                <p>
                  As they progress through the competitions, the goal is to
                  reach higher levels, such as state and even the VEX Robotics
                  World Championship. This competitive journey pushes students
                  to continually improve their robots and strategies, fostering
                  skills in engineering, programming, collaboration, and
                  perseverance.
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* What We Offer Section */}
        <Section id="what-we-offer">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-100 mb-4">
              Our Programs
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From beginners to advanced competitors, we have programs designed
              for every skill level
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              className="group bg-neutral-800/50 rounded-2xl p-6 border border-neutral-700 hover:border-brand-500/50 transition-all duration-75 hover:shadow-[0_20px_60px_rgba(0,0,0,0.6)] cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ y: -2 }}
            >
              <Link to="/iq" className="block">
                <div className="w-full aspect-square mb-6 rounded-xl overflow-hidden group-hover:scale-102 transition-transform duration-75">
                  <img
                    src="/media/vex-iq-program.jpeg"
                    alt="VEX IQ Robotics Program"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">
                    VEX IQ
                  </h3>
                  <p className="text-brand-400 font-medium mb-4">
                    Elementary & Middle School
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    VEX IQ Robotics is a hands-on program for elementary and
                    middle school students, where they build and program robots
                    to solve challenges. Using easy-to-assemble parts and simple
                    programming, students learn STEM concepts while fostering
                    creativity, teamwork, and problem-solving skills.
                  </p>
                </div>
              </Link>
            </motion.div>

            <motion.div
              className="group bg-neutral-800/50 rounded-2xl p-6 border border-neutral-700 hover:border-brand-500/50 transition-all duration-75 hover:shadow-[0_20px_60px_rgba(0,0,0,0.6)] cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              whileHover={{ y: -2 }}
            >
              <Link to="/v5" className="block">
                <div className="w-full aspect-square mb-6 rounded-xl overflow-hidden group-hover:scale-102 transition-transform duration-75">
                  <img
                    src="/media/vex-v5-program.jpeg"
                    alt="VEX V5 Robotics Program"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">
                    VEX V5
                  </h3>
                  <p className="text-brand-400 font-medium mb-4">
                    Middle & High School
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    VEX V5 Robotics is a competitive program for middle and high
                    school students, where teams design, build, and program
                    advanced robots. Using powerful motors and sensors, students
                    develop problem-solving, engineering, and strategic skills
                    in exciting competitions.
                  </p>
                </div>
              </Link>
            </motion.div>

            <motion.div
              className="group bg-neutral-800/50 rounded-2xl p-6 border border-neutral-700 hover:border-brand-500/50 transition-all duration-75 hover:shadow-[0_20px_60px_rgba(0,0,0,0.6)] cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ y: -2 }}
            >
              <Link to="/summer-camp" className="block">
                <div className="w-full aspect-square mb-6 rounded-xl overflow-hidden group-hover:scale-102 transition-transform duration-75">
                  <img
                    src="/media/summer-camp-program.jpeg"
                    alt="Summer Camp Program"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-100 mb-2">
                    Summer Camp
                  </h3>
                  <p className="text-brand-400 font-medium mb-4">
                    Intensive Learning
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    In our VEX IQ Robotics Summer Camps, students work in teams
                    of 3–5 to build, program, and document a robot for a game
                    challenge. Over six days, they'll develop their skills, and
                    on the seventh day, they'll compete for awards in areas like
                    design, programming, and teamwork.
                  </p>
                </div>
              </Link>
            </motion.div>
          </div>
        </Section>

        {/* CTA Section */}
        <Section>
          <div className="text-center bg-gradient-to-r from-neutral-800/50 to-neutral-700/50 rounded-2xl p-12 border border-neutral-600">
            <motion.h2
              className="text-3xl md:text-4xl font-bold mb-6 text-gray-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Book a Free Trial Session
            </motion.h2>
            <motion.p
              className="text-xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              We believe students should love robotics. Come try robotics for
              free at one of our meetings, where students learn about how VEX
              robotics works, drive already built robots, and even possibly
              build a system of their own.
            </motion.p>
            <motion.div
              className="flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <motion.button
                data-cal-namespace="free-trial"
                data-cal-link="aplakosh/free-trial"
                data-cal-config='{"layout":"month_view"}'
                className="rounded-lg bg-brand-600 text-neutral-900 font-semibold px-10 py-4 text-xl transition-all duration-150 hover:scale-105 hover:bg-brand-500 active:scale-95 shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Try for free
              </motion.button>
            </motion.div>
            <FreeTrialSchedule className="mt-4" />
          </div>
        </Section>
      </div>
    </>
  );
}
